// Asigna una inspección a empresas: por inspección puntual, o —si tiene
// módulo— por módulo completo (todas las inspecciones de ese módulo quedan
// disponibles para la empresa). Muestra lo ya asignado y permite quitarlo.
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Loader, AlertCircle, Check, Trash2, Send, CheckCircle } from "lucide-react";
import { api } from "../api";
import { parseEmailList } from "../utils/emails";

export default function InspectionAssignModal({ inspection, moduleName, onClose }) {
  const [companies, setCompanies] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selected, setSelected] = useState([]);
  const [scope, setScope] = useState("inspection"); // "inspection" | "module"
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [emailRows, setEmailRows] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.all([api.getCompanies(), api.getInspectionAssignments()]).then(([c, a]) => {
      setCompanies(Array.isArray(c) ? c : []);
      setAssignments(Array.isArray(a) ? a : []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const inspectionAssignments = assignments.filter(a => a.inspection_id === inspection.id);
  const moduleAssignments = inspection.module_id ? assignments.filter(a => a.module_id === inspection.module_id) : [];
  const currentList = scope === "module" ? moduleAssignments : inspectionAssignments;
  const assignedCompanyIds = new Set(currentList.map(a => a.company_id));

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleAssign = async () => {
    if (selected.length === 0) return;
    setSaving(true);
    setError(null);
    const result = scope === "module"
      ? await api.assignInspectionModule(inspection.module_id, selected)
      : await api.assignInspection(inspection.id, selected);
    setSaving(false);
    if (result.error) { setError(result.error); return; }
    if (scope === "inspection" && result.links) {
      setEmailRows(result.links.map((link) => {
        const company = companies.find((item) => item.id === link.company_id);
        const url = `${window.location.origin}/?public_inspection_token=${link.token}`;
        return { companyId: link.company_id, companyName: company?.name || "—", url, to: "", status: null, error: "" };
      }));
    }
    setSelected([]);
    load();
  };

  const updateEmailRow = (companyId, changes) => setEmailRows((rows) => rows.map((row) => row.companyId === companyId ? { ...row, ...changes } : row));
  const sendLink = async (row) => {
    const { valid: to } = parseEmailList(row.to);
    if (!to.length) return;
    updateEmailRow(row.companyId, { status: "sending", error: "" });
    const subject = `[SST] Inspección: ${inspection.name}`;
    const body = `Estimado(a),\n\nLe compartimos la inspección "${inspection.name}". Por favor ingrese al siguiente enlace para diligenciarla:\n${row.url}\n\nAtentamente,\nÁrea de Seguridad y Salud en el Trabajo`;
    try {
      const result = await api.sendInspectionLinkEmail(inspection.id, { company_id: row.companyId, to, subject, body, url: row.url });
      if (result.error) throw new Error(result.error);
      updateEmailRow(row.companyId, { status: result.failed?.length ? "partial" : "ok", result });
    } catch (err) { updateEmailRow(row.companyId, { status: "error", error: err.message }); }
  };

  const handleRemove = async (assignmentId) => {
    await api.deleteInspectionAssignment(assignmentId);
    load();
  };

  return (
    <motion.div className="modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <motion.div className="modal modal-large"
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}>

        <div className="modal-header">
          <h2>Asignar "{inspection.name}"</h2>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        {loading ? (
          <div style={{ padding: 60, display: "flex", justifyContent: "center" }}>
            <Loader size={28} className="spin" opacity={0.4} />
          </div>
        ) : (
          <>
            <div className="modal-form" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {emailRows.length > 0 && (
                <div style={{ border: "1px solid var(--primary)", background: "#F0FDFA", borderRadius: 10, padding: 12, marginBottom: 14 }}>
                  <p style={{ fontWeight: 700, fontSize: "0.86rem", marginBottom: 4 }}>Enlaces generados</p>
                  <p style={{ fontSize: "0.76rem", color: "var(--text-muted)", marginBottom: 10 }}>Copia el enlace o envíalo por correo a cada empresa.</p>
                  {emailRows.map((row) => {
                    const { valid } = parseEmailList(row.to);
                    return <div key={row.companyId} style={{ borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 10 }}>
                      <p style={{ fontSize: "0.82rem", fontWeight: 600 }}>{row.companyName}</p>
                      <input className="form-input" readOnly value={row.url} onFocus={(event) => event.target.select()} style={{ fontSize: "0.7rem", marginTop: 6 }} />
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <input className="form-input" type="email" placeholder="correo@empresa.com (varios separados por coma)" value={row.to} onChange={(event) => updateEmailRow(row.companyId, { to: event.target.value })} />
                        <button className="btn-primary" onClick={() => sendLink(row)} disabled={!valid.length || row.status === "sending"}>
                          {row.status === "sending" ? <Loader size={14} className="spin" /> : <Send size={14} />} Enviar
                        </button>
                      </div>
                      {row.status === "ok" && <span style={{ color: "var(--primary)", fontSize: "0.75rem", display: "inline-flex", gap: 4, marginTop: 6 }}><CheckCircle size={13} /> Enviado</span>}
                      {row.status === "partial" && <span style={{ color: "#B45309", fontSize: "0.75rem" }}>Se enviaron {row.result.sent} de {row.result.total}</span>}
                      {row.status === "error" && <span style={{ color: "#C53030", fontSize: "0.75rem" }}>{row.error}</span>}
                    </div>;
                  })}
                </div>
              )}
              {inspection.module_id && (
                <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                  <button
                    className={scope === "inspection" ? "btn-primary" : "btn-secondary"}
                    onClick={() => { setScope("inspection"); setSelected([]); }}
                    style={{ flex: 1, justifyContent: "center" }}
                  >
                    Solo esta inspección
                  </button>
                  <button
                    className={scope === "module" ? "btn-primary" : "btn-secondary"}
                    onClick={() => { setScope("module"); setSelected([]); }}
                    style={{ flex: 1, justifyContent: "center" }}
                  >
                    Módulo completo{moduleName ? ` (${moduleName})` : ""}
                  </button>
                </div>
              )}

              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                {scope === "module"
                  ? "La empresa verá todas las inspecciones de este módulo, incluidas las que se creen después."
                  : "La empresa solo verá esta inspección puntual."}
              </p>

              {companies.map(c => {
                const assigned = assignedCompanyIds.has(c.id);
                const assignment = currentList.find(a => a.company_id === c.id);
                return (
                  <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", cursor: assigned ? "default" : "pointer" }}>
                    {assigned ? (
                      <span style={{ width: 16, display: "flex", justifyContent: "center" }}><Check size={14} style={{ color: "var(--primary)" }} /></span>
                    ) : (
                      <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
                    )}
                    <span style={{ flex: 1 }}>{c.name}</span>
                    {assigned && (
                      <button className="btn-icon" title="Quitar asignación" onClick={() => handleRemove(assignment.id)}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </label>
                );
              })}
              {companies.length === 0 && <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>No hay empresas creadas.</p>}
              {error && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FEE2E2", borderRadius: 8, color: "#C53030", fontSize: "0.78rem" }}>
                  <AlertCircle size={15} /> {error}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Cerrar</button>
              <button className="btn-primary" onClick={handleAssign} disabled={selected.length === 0 || saving}>
                {saving ? <><Loader size={14} className="spin" /> Asignando...</> : "Asignar"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
